import React from 'react';

// Note: TinyMCE is loaded dynamically oncomponent mount (see `componentWillMount`)
// New plugins also need to be added to client/thirdparty/tinymce.ts
import { Editor as TinyMCE } from '@tinymce/tinymce-react';

import './TinyEditor.scss';
import { IAllProps, IProps } from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor';
import { BubbleOverlay } from './BubbleUI';

type TinyMCEConfig = IProps['init'] & {
  selector?: undefined;
  target?: undefined;
};

type TinyMCEMode = 'html' | 'text' | 'text-link';

function TINYMCE_CONFIG(mode: TinyMCEMode) {
  let toolbar = '';
  let plugins = '';
  let paste_as_text = false;

  switch (mode) {
    case 'text':
      toolbar = 'bold italic underline | bullist numlist';
      plugins = 'lists paste';
      paste_as_text = true;
      break;
    case 'text-link':
      toolbar = 'bold italic underline | bullist numlist | link';
      plugins = 'link lists paste';
      paste_as_text = true;
      break;
    default:
      toolbar =
        'formatselect | bold italic underline removeformat | bullist numlist | ' +
        'alignleft aligncenter alignright | link table';
      plugins = 'link table lists paste';
      paste_as_text = false;
      break;
  }

  const config: TinyMCEConfig = {
    menubar: false,
    statusbar: false,
    contextmenu: false,
    convert_urls: false,
    skin: false,
    paste_as_text,
    plugins,
    toolbar,
    table_toolbar:
      'tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow ' +
      '| tableinsertcolbefore tableinsertcolafter tabledeletecol',
    browser_spellcheck: true,

    // WARNING! It is important that this matches the webpack's generated stylesheet, because
    // TinyMCE tries to download it and apply it to the editor's iframe when instantiated
    content_css: '/styles-main.css',
  };

  return config;
}

export interface EditorPropsBase {
  value: string;
  onValue?: (value: string) => void;
  mode?: TinyMCEMode;
  config?: (init: TinyMCEConfig) => TinyMCEConfig;
  subtitle?: string;
}

export interface TinyEditorState {
  editor?: object;
}

type TinyEditorProps = EditorPropsBase &
  React.HTMLAttributes<HTMLDivElement> &
  Pick<IAllProps, 'textareaName' | 'disabled' | 'onBlur'>;

export default class TinyEditor extends React.Component<TinyEditorProps, TinyEditorState> {
  public readonly state: TinyEditorState;

  constructor(props: TinyEditorProps) {
    super(props);

    this.state = {};
  }

  public async componentWillMount() {
    return import(/* webpackChunkName: "editor" */ '../thirdparty/tinymce').then((editor: any) => {
      this.setState({ editor });
    });
  }

  public render() {
    const { value, onValue, config, mode, subtitle, textareaName, disabled, onBlur, ...rest } =
      this.props;
    const init = config ? config(TINYMCE_CONFIG(mode || 'html')) : TINYMCE_CONFIG(mode || 'html');

    return this.state.editor ? (
      <div {...rest}>
        <TinyMCE
          value={this.props.value}
          init={init}
          onEditorChange={this.props.onValue}
          textareaName={textareaName}
          disabled={disabled}
          onBlur={onBlur}
        />
        {subtitle && <div className="text-xs text-smoke italic">{subtitle}</div>}
      </div>
    ) : (
      <BubbleOverlay loading />
    );
  }
}
